<!--
// app/components/layout/header/LangSwitcher.vue
-->
<script setup lang="ts">
import type { Locale } from "@intlify/core-base";
import { useLocalesStore } from "~/app/stores/localesStore";
import { SwitchLocalePathLink } from "#components";
import nuxtLink from "#app/components/nuxt-link";

const { locale, locales } = useI18n();
const error = useError();
const switchLocalePath = useSwitchLocalePath();
const router = useRouter();
const route = useRoute();
const localesStore = useLocalesStore();
const languagesContainerMobile = ref(false);

const availableLocales = computed(() => {
  return locales.value.filter(i => i.code !== locale.value);
});

const isNewsPage = computed(() => {
  return route.params.news && !route.params.post;
});

const cleanNewsPath = (lang: Locale): string => {
  const isPaginatedPage = isNewsPage && route.query.page;

  if (isPaginatedPage) {
    return switchLocalePath(lang).split("?page=")[0];
  } else {
    return switchLocalePath(lang);
  }
};

const toggleLanguagesContainerMobile = () => {
  languagesContainerMobile.value = !languagesContainerMobile.value;
};
</script>

<template>
  <div v-if="!error || error.statusCode !== 404" class="bg-gray-900 border-b border-b-gray-700">
    <button class="nav-mobile-language-item" @click="toggleLanguagesContainerMobile">
      {{ $t("header.dropbarCompetition.language") }}
      <span class="font-semibold text-sm py-1 text-center flex items-center text-gray-500"
        >{{ locale }}
        <svg
          class="w-3 h-3 ml-2 rtl:mr-2 rtl:ml-0"
          aria-hidden="true"
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 10 6"
        >
          <path
            stroke="currentColor"
            stroke-linecap="round"
            stroke-linejoin="round"
            stroke-width="2"
            d="m1 1 4 4 4-4"
          /></svg
      ></span>
    </button>
    <div
      :class="languagesContainerMobile ? 'h-auto' : 'h-0'"
      class="transition-[height] duration-300 ease-in-out overflow-hidden"
    >
      <!--News switcher-->
      <ul v-if="isNewsPage" class="text-sm font-normal text-gray-300 flex items-center flex-wrap">
        <li v-for="avLocale in availableLocales" :key="avLocale.code">
          <component
            :is="
              localesStore.currentRegionAvailableNewsLocales &&
              localesStore.currentRegionAvailableNewsLocales.includes(avLocale.code)
                ? nuxtLink
                : 'span'
            "
            class="block p-2 px-4 hover:text-white uppercase"
            :class="[
              {
                '!text-gray-700 !cursor-default hover:!text-gray-700 line-through':
                  !localesStore.currentRegionAvailableNewsLocales?.includes(avLocale.code),
              },
            ]"
            no-prefetch
            :to="cleanNewsPath(avLocale.code)"
            @click="toggleLanguagesContainerMobile"
          >
            {{ avLocale.code }}
          </component>
        </li>
      </ul>

      <!--Generic switcher-->
      <ul v-else class="text-sm font-normal text-gray-300 flex items-center flex-wrap">
        <li v-for="avLocale in availableLocales" :key="avLocale.code">
          <switch-locale-path-link
            :locale="avLocale.code"
            class="block p-2 px-4 hover:text-white uppercase"
            :class="[
              {
                '!text-gray-700 !cursor-default hover:!text-gray-700 line-through': !switchLocalePath(
                  avLocale.code
                ),
              },
            ]"
            no-prefetch
            @click="toggleLanguagesContainerMobile"
          >
            {{ avLocale.code }}
          </switch-locale-path-link>
        </li>
      </ul>
    </div>
  </div>
</template>

<style scoped>
.nav-mobile-language-item {
  @apply text-gray-400 flex justify-between py-1 px-4 text-sm items-center min-w-full;
}
</style>
